import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControl,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Tooltip,
  useTheme,
} from '@mui/material';
import { darken, transparentize } from 'polished';
import styled from 'styled-components';

import { defaultApi } from '@/api';
import { useResources } from '@/contexts/resource-context';
import { createRoadmapPublicLink } from '@/features/canvas/api';
import { PublishDialogFormContent } from '@/features/canvas/components/PublishDialogFormContent';
import { PUBLIC_LINK_TITLE_MAX_LENGTH } from '@/features/canvas/constants';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import { createRoadmapPublicLinkUrl } from '@/features/canvas/utils/create-roadmap-public-link-url';
import { getPublicLinks } from '@/features/publicLinks/api';
import { Form } from '@/features/ui/components/Form';
import { useHandleCopyUrl } from '@/hooks/useHandleCopyUrl';
import { useShowToast } from '@/hooks/useShowToast';
import { components } from '@/types/api';

import { TenantEntities } from '../../api/tenant-sso';
import PublishDialogFormSso from '../PublishDialogFormSso/PublishDialogFormSso';

interface PublishDialogFormState {
  groups: number[];
  password: string;
  title: string;
  users: number[];
}

interface PublicLinkData {
  id: number;
  roadmap_id: number;
  title: string;
  url: string;
  auth_type?: string;
  created_at?: string;
  groups?: {
    id: number;
    name: string;
  }[];
  hasSso?: boolean;
  isLocked?: boolean;
  password?: string;
  publishDate?: string;
  updated_at?: string;
  viewers?: {
    email: string;
    id: number;
    lastname?: string;
    name?: string;
  }[];
}

interface DropdownOption {
  label: string;
  publicLink: PublicLinkData | null;
  value: string | number;
}

const PublishDialogForm = () => {
  const { resources } = useResources();
  const theme = useTheme();
  const { t } = useTranslation();
  const { handleCopyUrl } = useHandleCopyUrl();
  const { showToast } = useShowToast();
  const { roadmap, updateLastSavedTime } = useEditorContext();
  const queryClient = useQueryClient();
  const [selectedEntities, setSelectedEntities] = useState<TenantEntities>({
    groups: [],
    viewers: [],
  });

  const [roadmapPublicLinkUrl, setRoadmapPublicLinkUrl] = useState('');
  const [selectedPublicLink, setSelectedPublicLink] = useState<PublicLinkData | null>(null);
  const [mode, setMode] = useState<'create' | 'update'>('create');
  // For SSO mode, start with empty title; for non-SSO, use roadmap title as default
  const [linkTitle, setLinkTitle] = useState(resources.is_sso ? '' : roadmap.title);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Fetch existing public links for SSO mode
  const { data: existingPublicLinks = [], isLoading: isLoadingLinks } = useQuery(
    ['publicLinks', roadmap.id],
    async () => {
      // Use the existing endpoint that now includes roadmap_id in response
      const roadmapLinks = await getPublicLinks();

      // Cast to unknown then to our type since the API response type doesn't match the actual data
      const typedLinks = roadmapLinks as unknown as (PublicLinkData & {
        hasSso?: boolean;
        roadmap_id?: number;
      })[];

      const roadmapIdNumber = Number(roadmap.id);

      // Filter for SSO links that belong to this roadmap
      const ssoLinks = typedLinks.filter(link => {
        const matchesRoadmap = link.roadmap_id === roadmapIdNumber;
        const isSSO = link.hasSso === true;

        return matchesRoadmap && isSSO;
      });

      return ssoLinks;
    },
    {
      enabled: resources.is_sso,
    }
  );

  // Create dropdown options
  const dropdownOptions: DropdownOption[] = [
    { value: 'new', label: '(Create new link)', publicLink: null },
    ...existingPublicLinks.map(link => {
      const option: DropdownOption = {
        value: link.id,
        label: link.title,
        publicLink: link,
      };

      return option;
    }),
  ];

  // Handle dropdown selection
  const handleLinkSelection = (value: string | number) => {
    if (value === 'new') {
      setSelectedPublicLink(null);
      setMode('create');
      // For SSO mode, clear title for new links; for non-SSO, use roadmap title
      setLinkTitle(resources.is_sso ? '' : roadmap.title);
      setRoadmapPublicLinkUrl('');
      // Reset selectedEntities for new links
      setSelectedEntities({ groups: [], viewers: [] });
    } else {
      const selected = dropdownOptions.find(opt => opt.value === value);
      if (selected?.publicLink) {
        setSelectedPublicLink(selected.publicLink);
        setMode('update');
        setLinkTitle(selected.publicLink.title);
        setRoadmapPublicLinkUrl(selected.publicLink.url); // Show URL immediately

        // Populate selectedEntities with existing viewers and groups
        // Transform API data to match TenantEntities interface
        setSelectedEntities({
          viewers: (selected.publicLink.viewers || []).map(viewer => ({
            id: viewer.id,
            name: viewer.name || '',
            lastname: viewer.lastname || '',
            email: viewer.email,
            isOwner: false,
            isEditable: true,
          })),
          groups: selected.publicLink.groups || [],
        });
      }
    }
  };

  // Copy link function
  const copyPublicLink = () => {
    if (selectedPublicLink) {
      const linkUrl = createRoadmapPublicLinkUrl(selectedPublicLink.url);
      handleCopyUrl({ url: linkUrl });
      showToast('success', t('editor.header.publish_dialog.link_copied'));
    }
  };

  const { mutateAsync: createRoadmapPublicLinkMutation, isLoading: isCreatingPublicLink } =
    useMutation(
      ({ newLink }: { newLink: components['schemas']['PublicLinkRequest'] }) =>
        createRoadmapPublicLink({
          roadmapId: Number(roadmap.id),
          newLink,
        }),
      {
        onSuccess: () => {
          updateLastSavedTime();
          // Invalidate and refetch the public links query to show the new link in dropdown
          void queryClient.invalidateQueries(['publicLinks', roadmap.id]);
        },
      }
    );

  // Add mutation for updating existing public link
  const { mutateAsync: updatePublicLinkMutation, isLoading: isUpdatingPublicLink } = useMutation(
    async ({ publicLinkId, title }: { publicLinkId: number; title?: string }) => {
      const { data } = await defaultApi.patch(
        `/roadmaps/${roadmap.id}/public-links/${publicLinkId}/snapshot`,
        { title }
      );
      return data;
    },
    {
      onSuccess: () => {
        updateLastSavedTime();
        showToast('success', t('editor.header.publish_dialog.link_updated'));
        // Invalidate and refetch the public links query to show updated link
        void queryClient.invalidateQueries(['publicLinks', roadmap.id]);
      },
    }
  );

  const onSubmit = async (formData: PublishDialogFormState) => {
    // For SSO mode, use the linkTitle from state, for non-SSO use formData.title
    const titleToUse = resources.is_sso ? linkTitle : formData.title;

    if (mode === 'update' && selectedPublicLink && selectedPublicLink.id) {
      // Update existing public link
      await updatePublicLinkMutation({
        publicLinkId: selectedPublicLink.id,
        title: titleToUse,
      });
      // Keep the existing URL
      setRoadmapPublicLinkUrl(selectedPublicLink.url);
    } else {
      // Create new public link
      const { url: publicLink } = await createRoadmapPublicLinkMutation({
        newLink: {
          ...formData,
          title: titleToUse,
          isLocked: true,
          groups: selectedEntities.groups.map(item => item.id),
          viewers: selectedEntities.viewers.map(item => item.id),
        },
      });
      setRoadmapPublicLinkUrl(publicLink);
    }
  };

  const roadmapPublicLink = createRoadmapPublicLinkUrl(roadmapPublicLinkUrl);
  const isProcessing = isCreatingPublicLink || isUpdatingPublicLink;

  return (
    <>
      <DialogContent
        sx={{
          color: theme.palette.brand.textPrimary,
          padding: '0 2rem',
          overflow: 'visible',
        }}
      >
        {/* Add dropdown for SSO mode only */}
        {resources.is_sso && (
          <Box sx={{ marginBottom: '2rem' }}>
            <Box
              sx={{
                marginBottom: '0.75rem',
                fontSize: '1.4rem',
                color: theme.palette.brand.textPrimary,
              }}
            >
              {t('editor.header.publish_dialog.link_title')}
            </Box>
            <FormControl fullWidth>
              <StyledAutocomplete
                slotProps={{
                  popper: {
                    sx: {
                      zIndex: 1300,
                      '& .MuiPaper-root': {
                        backgroundColor: '#ffffff',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '20px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2)',
                        overflowY: 'auto',
                      },
                    },
                  },
                }}
                value={
                  selectedPublicLink
                    ? {
                        label: linkTitle,
                        value: selectedPublicLink.id,
                        publicLink: selectedPublicLink,
                      }
                    : null
                }
                onChange={(
                  event: React.SyntheticEvent,
                  newValue: string | DropdownOption | null
                ) => {
                  if (newValue) {
                    if (typeof newValue === 'string') {
                      // User typed a custom value
                      setLinkTitle(newValue);
                    } else if (newValue.value === 'new') {
                      handleLinkSelection('new');
                    } else {
                      handleLinkSelection(newValue.value);
                    }
                  }
                }}
                inputValue={linkTitle}
                onInputChange={(event: React.SyntheticEvent, newInputValue: string) => {
                  setLinkTitle(newInputValue);
                }}
                options={dropdownOptions}
                getOptionLabel={(option: string | DropdownOption) => {
                  if (typeof option === 'string') return option;
                  return option.label;
                }}
                renderOption={(
                  props: React.HTMLAttributes<HTMLLIElement>,
                  option: DropdownOption
                ) => (
                  <Box component="li" {...props}>
                    {option.label}
                  </Box>
                )}
                isOptionEqualToValue={(option: DropdownOption, value: DropdownOption | null) => {
                  if (!value) return false;
                  return option.value === value.value;
                }}
                disabled={isLoadingLinks || isProcessing}
                freeSolo
                renderInput={(params: AutocompleteRenderInputParams) => (
                  <TextField
                    {...params}
                    placeholder="Enter link title"
                    inputProps={{
                      ...params.inputProps,
                      maxLength: PUBLIC_LINK_TITLE_MAX_LENGTH,
                    }}
                    // eslint-disable-next-line react/jsx-no-duplicate-props
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {selectedPublicLink && !roadmapPublicLinkUrl && (
                            <InputAdornment position="end">
                              <Tooltip title={t('editor.header.publish_dialog.copy_link')}>
                                <IconButton onClick={copyPublicLink} edge="end" size="small">
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          )}
                          <InputAdornment position="end">
                            <ChevronDownIcon width={20} height={20} />
                          </InputAdornment>
                        </>
                      ),
                    }}
                  />
                )}
              />
            </FormControl>
          </Box>
        )}

        <Form<PublishDialogFormState>
          defaultValues={{ title: linkTitle }}
          key={selectedPublicLink?.id || 'new'}
          onSubmit={formData => onSubmit(formData as unknown as PublishDialogFormState)}
        >
          {/* Show regular form content for non-SSO mode only */}
          {!resources.is_sso && <PublishDialogFormContent />}
          <Button ref={buttonRef} style={{ display: 'hidden' }} type="submit" />
        </Form>
        {resources.is_sso && (
          <PublishDialogFormSso
            selectedEntities={selectedEntities}
            setSelectedEntities={setSelectedEntities}
            isDisabled={!!roadmapPublicLinkUrl || mode === 'update'}
            isAccessPage={false}
          />
        )}

        {roadmapPublicLinkUrl && (
          <Box
            sx={{
              padding: '2rem',
              backgroundColor: theme.palette.brand.backgroundMain,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
              marginBottom: '3rem',
              borderRadius: '2rem',
              wordBreak: 'break-word',
            }}
          >
            <PublicLink>{roadmapPublicLink}</PublicLink>
            <ViewLinkButton
              type="button"
              onClick={e => {
                e.preventDefault();
                handleCopyUrl({ url: roadmapPublicLink });
                showToast('success', t('editor.header.publish_dialog.link_copied'));
              }}
            >
              {t('editor.header.publish_dialog.main_button_copy_link')}
            </ViewLinkButton>
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          fontSize: '2rem',
          padding: '0 2rem',
          gap: '2rem',
        }}
      >
        <>
          {!roadmapPublicLinkUrl && (
            <StyledPrimaryButton
              onClick={() => buttonRef.current?.click()}
              disabled={isProcessing}
              {...(isProcessing && {
                startIcon: <StyledLoader color="inherit" size={20} />,
              })}
            >
              {mode === 'update'
                ? t('editor.header.publish_dialog.main_button_update')
                : t('editor.header.publish_dialog.main_button_publish')}
            </StyledPrimaryButton>
          )}
          {roadmapPublicLinkUrl && mode === 'update' && (
            <StyledPrimaryButton
              type="button"
              onClick={() => buttonRef.current?.click()}
              disabled={isProcessing}
              {...(isProcessing && {
                startIcon: <StyledLoader color="inherit" size={20} />,
              })}
            >
              {t('editor.header.publish_dialog.main_button_update')}
            </StyledPrimaryButton>
          )}
          {roadmapPublicLinkUrl && mode === 'create' && (
            <StyledPrimaryButton
              type="button"
              onClick={() => {
                handleCopyUrl({ url: roadmapPublicLink });
              }}
            >
              {t('editor.header.publish_dialog.main_button_copy_link')}
            </StyledPrimaryButton>
          )}
        </>
      </DialogActions>
    </>
  );
};

const ViewLinkButton = styled(Link)`
  font-size: 1.5rem;
  text-decoration: none;
  color: ${({ theme }) => theme.palette.brand.textPrimary};
  background-color: ${({ theme }) => theme.palette.brand.white};
  padding: 0.5rem 2rem;
  font-weight: 600;
  border-radius: 2rem;
  box-shadow: ${({ theme }) => theme.shadows[0]};
  text-transform: none;
  white-space: nowrap;
  :hover {
    background-color: ${({ theme }) => darken(0.05, theme.palette.brand.white)};
  }
`;
const PublicLink = styled.div`
  font-weight: 700;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.palette.brand.textPrimary};
`;

const StyledPrimaryButton = styled(Button)`
  border-radius: 2rem;
  padding: 0.25rem 2rem;
  min-width: 180px;
  font-size: 1.75rem;
  font-weight: 700;
  text-transform: none;
  color: ${({ theme }) => theme.palette.brand.white};
  background-color: ${({ theme }) => theme.palette.brand.secondary};
  border: 1px solid ${({ theme }) => theme.palette.brand.secondary};
  :hover {
    background-color: ${({ theme }) => theme.palette.brand.backgroundInputFocus};
    color: ${({ theme }) => theme.palette.brand.secondary};
  }

  &.Mui-disabled {
    color: ${({ theme }) => transparentize(0.3, theme.palette.brand.white)};
  }
`;

const StyledLoader = styled(CircularProgress)`
  color: ${({ theme }) => theme.palette.brand.editorTabPrimary};
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const StyledAutocomplete = styled(Autocomplete as any)`
  & .MuiInputBase-root {
    font-size: 1.5rem;
    padding-top: 0;
    padding-bottom: 0;

    & .MuiInputBase-input {
      padding: 0.25rem 1.5rem; // Reduced from 1rem to 0.25rem (2.5px)
    }

    // Remove padding from the Autocomplete wrapper
    & .MuiAutocomplete-inputRoot {
      padding: 0;
    }
  }

  & .MuiOutlinedInput-root {
    padding: 0; // Remove any additional padding
    padding-right: 15px !important; // Reduce from default 39px

    & .MuiOutlinedInput-notchedOutline {
      border-color: #dcdcdc;
      top: 0; // Remove the -5px top offset
      // Disable the notch for label
      legend {
        display: none;
      }
    }

    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: #dcdcdc; // Keep same color on hover
    }

    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: #dcdcdc; // Keep same color on focus
      border-width: 1px; // Keep 1px width, don't increase
    }
  }

  & .MuiAutocomplete-option {
    font-size: 1.5rem;
  }

  // Hide the clear button (X)
  & .MuiAutocomplete-clearIndicator {
    display: none;
  }

  // Hide the default MUI dropdown arrow
  & .MuiAutocomplete-popupIndicator {
    display: none !important;
  }

  // Dropdown styling is now handled via slotProps in the component

  // Change input background when dropdown is open
  &.Mui-focused .MuiInputBase-root {
    background-color: #dcdcdc;
  }
`;

export { PublishDialogForm };
